import { cloneDeep } from 'lodash';

import {
  AttachmentFile,
  AudioVersion,
  FileType,
  ImageVersion,
  MimeType,
  StrokeVersion,
  TtsAudioVersion,
  VideoVersion,
} from '../../models/Base';
import { Mutable } from '../../models/Utils';
import { mimeTypeToExtension, toPlatformFileVersion } from '../../utils/file';
import { File, FileVersion } from './file';

type FileVersionDownloadJson = {
  readonly fileType: FileType;
  hash: string;
  expiredAt: number;
  url?: string | null;
  path?: string | null;
  error?: Error | null;
};

class FileVersionDownload {
  readonly fileType: FileType;
  readonly hash: string;
  readonly expiredAt: number;
  readonly url: string | null;
  readonly path: string | null;
  readonly error: Error | null;

  constructor(download: FileVersionDownloadJson) {
    this.fileType = download.fileType;
    this.hash = download.hash;
    this.expiredAt = download.expiredAt;
    this.url = download.url ?? null;
    this.path = download.path ?? null;
    this.error = download.error ?? null;
  }

  get isDeleted(): boolean {
    return this.error?.message === 'file not found';
  }

  get isUrlDownloaded(): boolean {
    return this.url !== null;
  }

  get isPathDownloaded(): boolean {
    return this.path !== null;
  }

  get usePath(): boolean {
    return (
      ConcreteFileDownload.needPath(this.fileType) || this.isPathDownloaded
    );
  }

  get isDownloaded(): boolean {
    return this.usePath ? this.isPathDownloaded : this.isUrlDownloaded;
  }

  get uri(): string | null {
    return this.usePath ? this.path : this.url;
  }

  isExpired(hash?: string): boolean {
    return (
      this.expiredAt < Date.now() || (hash !== undefined && hash !== this.hash)
    );
  }

  hasSameHash(hash: string) {
    return this.hash === hash;
  }

  update(download: FileVersionDownloadJson): void {
    const self = this as Mutable<this>;
    self.hash = download.hash;
    self.expiredAt = download.expiredAt;
    if (download.url !== undefined) self.url = download.url;
    if (download.path !== undefined) self.path = download.path;
    if (download.error !== undefined) self.error = download.error;
  }
}

export type FileDownloadResult<F extends File> = {
  file: F;
  version?: string;
  expiredAt: number;
  url?: string | null;
  path?: string | null;
  error?: Error | null;
};

export class ConcreteFileDownload<F extends File> {
  readonly file: F;
  private versions: Record<string, FileVersionDownload>;

  constructor(file: F) {
    this.file = cloneDeep(file);
    this.versions = {};
    if ('attachmentType' in file) {
      // init add public urls
      Object.entries(file.publicUrls ?? {}).forEach(([k, v]) => {
        this.versions[k] = new FileVersionDownload({
          fileType: file.attachmentType,
          hash: file.hash,
          expiredAt: Infinity,
          url: v,
        });
      });
    }
  }

  get id(): string {
    return ConcreteFileDownload.getId(this.file);
  }

  get fileType(): FileType {
    return ConcreteFileDownload.getFileType(this.file);
  }

  get hash(): string | undefined {
    return ConcreteFileDownload.getHash(
      this.file,
      ConcreteFileDownload.getDefaultVersionType(this.fileType),
    );
  }

  get isFailed(): false {
    return false;
  }

  getVersionName(v?: string): string {
    return toPlatformFileVersion(
      v ?? ConcreteFileDownload.getDefaultVersionType(this.fileType),
    );
  }

  getVersion(v?: string): FileVersionDownload | undefined {
    return this.versions[this.getVersionName(v)];
  }

  getExtension(v?: string): string {
    if ('attachmentType' in this.file) {
      return mimeTypeToExtension(this.file.mimeType);
    } else {
      const versionName = this.getVersionName(v);
      const versions = this.file.versions as Record<
        string,
        FileVersion<MimeType>
      >;
      const version = versions[versionName];
      if (version === undefined) {
        throw new Error(`No such verion - ${versionName}`);
      }
      return mimeTypeToExtension(version.mimeType);
    }
  }

  // getPath(v?: string) {
  //   const fileType =
  //     'attachmentType' in this.file ? 'attachment' : this.file.fileType;
  //   const id = this.id;
  //   const version = this.getVersionName(v);
  //   const extension = this.getExtension(v);
  //   return `file://${
  //     RNFS.DocumentDirectoryPath
  //   }/files/${fileType}/${id}/${version}${
  //     extension.length > 0 ? `.${extension}` : ''
  //   }`;
  // }

  update(download: FileDownloadResult<F>): void {
    const versionName =
      download.version ??
      ConcreteFileDownload.getDefaultVersionType(this.fileType);

    // clear up if file is changed
    const newHash = ConcreteFileDownload.getHash(download.file, versionName);
    const oldHash = ConcreteFileDownload.getHash(this.file, versionName);
    if (
      oldHash !== newHash ||
      Object.entries(this.versions).some(
        ([v, d]) => !d.hasSameHash(ConcreteFileDownload.getHash(this.file, v)),
      )
    ) {
      this.versions = {};
    }

    // update file
    const self = this as Mutable<this>;
    self.file = cloneDeep(download.file);

    // update version
    const versionDownload = {
      fileType: this.fileType,
      hash: ConcreteFileDownload.getHash(download.file, versionName),
      expiredAt: download.expiredAt,
      url: download.url,
      path: download.path,
      error: download.error,
    };
    const v = this.versions[versionName];
    if (v === undefined) {
      this.versions[versionName] = new FileVersionDownload(versionDownload);
    } else {
      v.update(versionDownload);
    }
  }

  static getId<F extends File>(
    file: F extends AttachmentFile
      ? Pick<F, 'contextType' | 'contextId' | 'attachmentType' | 'key'>
      : F extends Exclude<File, AttachmentFile>
      ? Pick<F, 'fileType' | 'fileId'>
      : never,
  ): string {
    if ('attachmentType' in file) {
      return `${file.contextType}_${file.contextId}_${file.attachmentType}_${file.key}`;
    } else {
      return `${file.fileType}_${file.fileId}`;
    }
  }

  static getHash<F extends File>(file: F, v?: string): string {
    if ('attachmentType' in file) {
      return file.hash;
    }

    let version;
    if (v) {
      const versions = file.versions as Record<string, FileVersion<MimeType>>;
      if (v in versions) {
        version = versions[v];
      }
    }
    if (!version) {
      version = Object.values<FileVersion<MimeType>>(file.versions)[0];
    }
    return version?.hash ?? '';
  }

  static getDefaultVersionType(fileType: FileType): string {
    switch (fileType) {
      case FileType.ResourceImage:
      case FileType.Image:
        return ImageVersion.Large;
      case FileType.Icon:
        return ImageVersion.Small;
      case FileType.Audio:
        return AudioVersion.source;
      case FileType.Video:
        return VideoVersion.hls;
      case FileType.Stroke:
        return StrokeVersion.source;
      case FileType.TtsAudio:
        return TtsAudioVersion.source;
    }
  }

  static needPath(fileType: FileType): boolean {
    return [FileType.Audio, FileType.Stroke, FileType.TtsAudio].includes(
      fileType,
    );
  }

  static getFileType(
    file: { attachmentType: FileType } | { fileType: FileType },
  ): FileType {
    if ('attachmentType' in file) {
      return file.attachmentType;
    } else {
      return file.fileType;
    }
  }
}

export type FileDownload<T extends File = File> = ConcreteFileDownload<T>;

export interface FileDownloadFailureJson {
  readonly id: string;
  error: Error;
}

export class FileDownloadFailure {
  readonly id: string;
  readonly error: Error;

  constructor(failure: FileDownloadFailureJson) {
    this.id = failure.id;
    this.error = failure.error;
  }

  get isFailed(): true {
    return true;
  }

  get isDeleted(): boolean {
    return this.error.message === 'file not found';
  }
}
